import { useEffect, useRef } from 'react';

/**
 * 모달/팝업이 열린 동안 하드웨어 뒤로가기(또는 브라우저 back)를
 * 라우팅이 아닌 팝업 닫기에 연결한다.
 *
 * ── 왜 되돌리지(history.back) 않나 ──
 *
 * 예전에는 팝업이 열릴 때 history 에 표식을 하나 밀어 넣고, 코드로 닫힐 때
 * 그 표식을 history.back() 으로 거뒀다. 문제는 그 back 이 즉시가 아니라
 * 큐에 들어간다는 점이다. 거두는 사이에 무언가 일어나면 엉뚱한 것을 뽑는다.
 *
 * 이 한 가지에서 같은 사고가 세 번 났다.
 *   · 설정에서 로그아웃 → 토큰은 지워지는데 화면은 설정 그대로
 *     (옮긴 뒤 예약된 back 이 뒤늦게 실행돼 되돌아옴)
 *   · 관광지 추가 → 지도 시트에서 「이 장소로 선택」 → 등록 창이 통째로 사라짐
 *     (안쪽 시트가 거둔 back 을 아직 열려 있는 부모가 받아 함께 닫힘)
 *   · 장소 메뉴에서 수정·삭제가 아무 일도 하지 않음
 *     (시트가 거둔 back 이 방금 열린 창의 표식을 대신 뽑음)
 *
 * 그래서 거두지 않는다. 코드로 닫을 때는 history 를 건드리지 않고, 남은
 * 표식은 "닫힌 자리" 로 표시해 둔다. 주소가 바뀌지 않는 표식이라 남아 있어도
 * 화면에는 아무 일도 일어나지 않는다.
 *
 * 남은 자리는 두 가지로 정리된다.
 *   · 다음 팝업이 그 자리를 다시 쓴다(replaceState) — 항목이 쌓이지 않는다
 *   · 열린 팝업이 없을 때 그 자리에 올라서면 뒤로가기를 한 칸 더 이어 준다
 *
 * ── 누가 닫히나 ──
 *
 * 되돌리지 않으므로 popstate 는 오직 사용자의 진짜 뒤로가기에서만 온다.
 * 그러면 규칙이 하나로 줄어든다 — <b>가장 위에 열려 있는 팝업</b>이 닫힌다.
 * (예전에는 "내 표식이 현재 상태인가" 로 내 차례를 가렸다. 그 판정은 거두는
 *  back 과 사용자의 back 을 구분하기 위한 것이었는데, 거두지 않으니 필요 없다.)
 */

type Sentinel = { __modal: number; __visit: number };

const isSentinel = (s: any): s is Sentinel => !!s && typeof s.__modal === 'number';

/** 지금 열려 있는 팝업들의 표식 — 넣은 순서가 곧 쌓인 순서다 */
const openTokens = new Set<number>();
let seq = 0;

/* 같은 화면에 머무르는 동안만 유효한 번호. 주소가 바뀌면 올라간다.
   남은 표식을 건너뛸지 가릴 때 쓴다 — 다른 화면의 항목까지 삼키면 안 된다. */
let visit = 0;
let lastPath = '';
const currentVisit = (): number => {
    const path = window.location.pathname + window.location.search;
    if (path !== lastPath) {
        lastPath = path;
        visit += 1;
    }
    return visit;
};

/* 지금 서 있는 항목이 "닫힌 자리" 일 때의 표식.
   X 로 닫고 곧바로 뒤로가기를 누르면 그 한 칸은 사용자가 의도한 이동이 아니다. */
let staleHere: number | null = null;
let staleVisit = 0;

let skipperReady = false;
/** 닫힌 팝업이 남긴 자리는 사용자의 뒤로가기를 한 칸 더 이어 준다 */
const installSkipper = () => {
    if (skipperReady) return;
    skipperReady = true;
    window.addEventListener('popstate', () => {
        const v = currentVisit();
        /* 열린 팝업이 있으면 이 뒤로가기는 그 팝업의 몫이다.
           (이 리스너가 먼저 등록되므로 아래 handlePop 보다 항상 앞서 돈다) */
        if (openTokens.size > 0) return;

        /* ① 서 있던 자리가 닫힌 자리였다 — 그 한 칸은 헛걸음이니 이어 준다 */
        if (staleHere !== null && staleVisit === v) {
            staleHere = null;
            window.history.back();
            return;
        }

        /* ② 올라선 자리가 닫힌 자리다 — 같은 화면에서 생긴 것일 때만 건너뛴다.
               다른 화면에서 남은 표식은 그 화면으로 돌아온 정상적인 뒤로가기다. */
        const s = window.history.state;
        if (isSentinel(s) && !openTokens.has(s.__modal) && s.__visit === v) {
            window.history.back();
        }
    });
};

/** 현재 항목에 내 표식을 남긴다 — 닫힌 자리가 있으면 그 자리를 다시 쓴다 */
const mark = (token: number) => {
    const cur = window.history.state;
    /* 표식만 남기고 기존 state 는 물려받지 않는다.
       물려받으면 다른 기능이 자기 표식(예: placeExploreMapMode)으로 쓰는 값이
       이 항목에도 딸려와, 그쪽이 "내 자리" 로 잘못 알아보고 거둬 간다. */
    const next = { __modal: token, __visit: currentVisit() };
    if (isSentinel(cur) && !openTokens.has(cur.__modal)) {
        window.history.replaceState(next, '');
    } else {
        window.history.pushState(next, '');
    }
    staleHere = null;   // 지금 서 있는 자리는 더 이상 닫힌 자리가 아니다
};

export const useBackDismiss = (open: boolean, onClose: () => void) => {
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!open) return;

        installSkipper();
        const myToken = ++seq;
        openTokens.add(myToken);
        mark(myToken);

        let fired = false;
        const handlePop = () => {
            if (fired) return;                                  // 한 번만 닫는다
            const top = Array.from(openTokens).pop();
            if (top !== myToken) return;                        // 내 위에 더 있다
            fired = true;
            /* 바로 비워 준다 — 연달아 누르면 그 다음 팝업이 이어서 닫히도록.
               (아래 cleanup 에서도 지우지만 그쪽은 다음 렌더 뒤에 온다) */
            openTokens.delete(myToken);
            onCloseRef.current();
        };
        window.addEventListener('popstate', handlePop);

        return () => {
            openTokens.delete(myToken);
            window.removeEventListener('popstate', handlePop);
            /* history 를 되돌리지 않는다 — 위 주석의 사고 세 건이 전부 여기서 났다.
               남은 표식은 다음 팝업이 다시 쓰거나, 뒤로가기 때 건너뛴다.
               지금 서 있는 자리가 내 표식이면 "닫힌 자리" 로 적어 둔다 —
               다음 뒤로가기가 그 한 칸에서 헛돌지 않도록. */
            if (!fired && window.history.state?.__modal === myToken) {
                staleHere = myToken;
                staleVisit = currentVisit();
            }
        };
    }, [open]);
};
